// Meta (Facebook) Pixel helper
// Pixel ID: 1129972809096366 — initialized in public/index.html

/**
 * Fires an fbq('track', ...) event.
 * @param {string} eventName - "Lead", "Contact", "PageView", "ViewContent"…
 * @param {object} params - Meta standard params (content_name, value, currency…)
 * @param {string} [eventID] - Optional dedup ID; must be identical to server-side event_id
 */
export function trackFbEvent(eventName, params = {}, eventID) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    if (eventID) {
      window.fbq("track", eventName, params, { eventID });
    } else {
      window.fbq("track", eventName, params);
    }
  } catch (err) {
    // Never break UI on pixel failures. Log for observability.
    // eslint-disable-next-line no-console
    console.warn("[fbpixel] track failed:", eventName, err);
  }
}

export function trackFbCustomEvent(eventName, params = {}, eventID) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    if (eventID) {
      window.fbq("trackCustom", eventName, params, { eventID });
    } else {
      window.fbq("trackCustom", eventName, params);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[fbpixel] trackCustom failed:", eventName, err);
  }
}

/** Read the Meta browser cookies used for CAPI deduplication (fbp / fbc). */
export function readFbCookies() {
  if (typeof document === "undefined") return { fbp: null, fbc: null };
  const cookies = document.cookie.split("; ").reduce((acc, row) => {
    const [k, v] = row.split("=");
    if (k) acc[k] = v;
    return acc;
  }, {});
  return { fbp: cookies["_fbp"] || null, fbc: cookies["_fbc"] || null };
}

/** Read `fbclid` from the current URL (or null). */
export function getFbclidFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const p = new URLSearchParams(window.location.search);
    const v = p.get("fbclid");
    return v && v.trim() ? v.trim() : null;
  } catch (_) {
    return null;
  }
}

/**
 * Persist a stable external_id in localStorage on first visit. Returned RAW
 * (unhashed) — the backend hashes it before shipping to Meta.
 */
const EXTERNAL_ID_KEY = "sircs_ext_id";
export function getOrCreateExternalId() {
  if (typeof window === "undefined") return null;
  try {
    let v = window.localStorage.getItem(EXTERNAL_ID_KEY);
    if (v && v.length > 4) return v;
    v = generateEventId();
    window.localStorage.setItem(EXTERNAL_ID_KEY, v);
    return v;
  } catch (_) {
    return null;
  }
}

/** Generates a UUID v4, prefers crypto.randomUUID if available. */
export function generateEventId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Assemble the shared attribution payload sent to our CAPI endpoints so the
 * server can enrich the event with fbp/fbc/fbclid/external_id.
 */
export function buildAttributionPayload() {
  const { fbp, fbc } = readFbCookies();
  return {
    fbp,
    fbc,
    fbclid: getFbclidFromUrl(),
    external_id: getOrCreateExternalId(),
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
  };
}

/**
 * Fire pixel events synchronously, wait 300 ms so the request leaves the browser,
 * and only then run the given side-effect (typically window.location.href navigation).
 */
export function trackThenNavigate(fireEvents, navigate, delayMs = 300) {
  try {
    fireEvents();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[fbpixel] pre-nav track failed:", err);
  }
  setTimeout(() => {
    try {
      navigate();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[fbpixel] navigate failed:", err);
    }
  }, delayMs);
}
